import {
  acknowledgeDiagTrace,
  readDiagTrace,
  safeDiagnosticUrl,
  type DiagEntry,
} from './diag-trace'
import { diagnosticSessionId } from './context'
import { redactPii } from './redact'

export { redactPii } from './redact'

const DEFAULT_ENDPOINT =
  (import.meta.env.VITE_API_BASE_URL ?? 'https://api.spotty.one') + '/diag/trace'
const MAX_BATCH_BYTES = 48_000
const REQUEST_TIMEOUT_MS = 5000
let inFlight: Promise<void> | undefined
let retryAt = 0
let failures = 0

export interface DiagShipperConfig {
  endpoint?: string
  /** Public ingest credential, never a privileged backend secret. */
  token?: string
  releaseSha?: string
}

function createBatch(config: DiagShipperConfig) {
  const snapshot: DiagEntry[] = []
  const payload = {
    sessionId: diagnosticSessionId(),
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent.slice(0, 512),
    url: safeDiagnosticUrl(window.location.href).slice(0, 512),
    releaseSha: config.releaseSha,
    entries: [] as unknown[],
  }
  for (const entry of readDiagTrace()) {
    let safeEntry = redactPii(entry)
    if (new TextEncoder().encode(JSON.stringify(safeEntry)).length > 7000) {
      safeEntry = { ts: entry.ts, label: entry.label.slice(0, 128), data: { truncated: true } }
    }
    const next = { ...payload, entries: [...payload.entries, safeEntry] }
    if (new TextEncoder().encode(JSON.stringify(next)).length > MAX_BATCH_BYTES) break
    snapshot.push(entry)
    payload.entries.push(safeEntry)
  }
  return { snapshot, body: JSON.stringify(payload) }
}

async function sendBatches(config: DiagShipperConfig): Promise<void> {
  // Bound work even if other tabs keep adding events. Periodic flush drains the rest.
  for (let batch = 0; batch < 4; batch += 1) {
    const { snapshot, body } = createBatch(config)
    if (!snapshot.length) return
    const controller = new AbortController()
    const timeout = setTimeout(() => {
      controller.abort()
    }, REQUEST_TIMEOUT_MS)
    try {
      const response = await fetch(config.endpoint ?? DEFAULT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(config.token ? { 'X-Diag-Token': config.token } : {}),
        },
        body,
        keepalive: true,
        credentials: 'omit',
        signal: controller.signal,
      })
      if (!response.ok) throw new Error('Diagnostic ingest rejected the batch')
      const ack = (await response.json()) as { ok?: boolean; received?: number }
      if (ack.ok !== true || ack.received !== snapshot.length)
        throw new Error('Diagnostic ingest did not acknowledge the batch')
      acknowledgeDiagTrace(snapshot)
      failures = 0
      retryAt = 0
    } catch {
      failures += 1
      retryAt = Date.now() + Math.min(60_000, 1000 * 2 ** Math.min(failures, 6))
      return
    } finally {
      clearTimeout(timeout)
    }
  }
}

/**
 * Non-blocking for callers. Persist until the server acknowledges receipt, including
 * on pagehide. An interrupted response can cause duplicates; spin eventIds correlate them.
 */
export function flushDiagTrace(config: DiagShipperConfig = {}): Promise<void> {
  if (inFlight) return inFlight
  if (typeof window === 'undefined' || Date.now() < retryAt) return Promise.resolve()
  inFlight = sendBatches(config)
    .catch(() => {
      // Storage, serialization, and network errors must never affect application flow.
    })
    .finally(() => {
      inFlight = undefined
    })
  return inFlight
}
